import React, { useEffect, useState } from 'react';
import { DesignStorageService } from '../../utils/DesignStorageService';

export default function DesignBrowserModal({ isOpen, onClose, onSelect }) {
    const [designs, setDesigns] = useState([]);

    useEffect(() => {
        const loadDesigns = async () => {
            if (isOpen) {
                try {
                    const accountId = localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID';
                    const allDesigns = await DesignStorageService.getAllDesigns(accountId);
                    if (Array.isArray(allDesigns)) {
                        // Ordenar por fecha de actualización descendente
                        allDesigns.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

                        // Regenerar thumbnails si están vacíos o corruptos
                        const regenerationPromises = allDesigns.map(async (design) => {
                            // Si el thumbnail está vacío o es un placeholder genérico
                            if (!design.thumbnail || design.thumbnail.length < 100 || design.thumbnail.includes('placeholder')) {
                                // Intentar regenerar desde canvasData
                                if (design.canvasData && design.canvasData.front) {
                                    try {
                                        // Crear un canvas temporal para renderizar
                                        const tempCanvas = document.createElement('canvas');
                                        tempCanvas.width = 640;
                                        tempCanvas.height = 400;

                                        // Generar thumbnail desde los datos del canvas usando Promise
                                        const newThumbnail = await new Promise((resolve, reject) => {
                                            const fabric = window.fabric;
                                            if (!fabric) {
                                                reject(new Error('Fabric not loaded'));
                                                return;
                                            }

                                            const fabricCanvas = new fabric.StaticCanvas(tempCanvas);
                                            fabricCanvas.loadFromJSON(design.canvasData.front, () => {
                                                try {
                                                    const thumbnail = fabricCanvas.toDataURL({
                                                        format: 'png',
                                                        multiplier: 0.5,
                                                        quality: 0.8
                                                    });
                                                    fabricCanvas.dispose();
                                                    resolve(thumbnail);
                                                } catch (err) {
                                                    fabricCanvas.dispose();
                                                    reject(err);
                                                }
                                            }, (err) => {
                                                fabricCanvas.dispose();
                                                reject(err);
                                            });
                                        });

                                        design.thumbnail = newThumbnail;

                                        // Guardar el diseño actualizado (sin await para no bloquear)
                                        DesignStorageService.saveDesign(design).catch(err => {
                                            console.warn('Failed to save regenerated thumbnail:', err);
                                        });
                                    } catch (err) {
                                        console.warn('Failed to regenerate thumbnail for design:', design.name, err);
                                    }
                                }
                            }
                            return design;
                        });

                        // Esperar a que todas las regeneraciones terminen
                        const designsWithValidThumbnails = await Promise.all(regenerationPromises);
                        setDesigns(designsWithValidThumbnails);
                    } else {
                        console.error('Expected array of designs but got:', allDesigns);
                        setDesigns([]);
                    }
                } catch (error) {
                    console.error('Error loading designs:', error);
                    setDesigns([]);
                }
            }
        };
        loadDesigns();
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Abrir Diseño</h2>
                        <p className="text-sm text-gray-500">Selecciona un diseño guardado para continuar editando</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    {designs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-lg">No hay diseños guardados</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {designs.map(d => (
                                <div
                                    key={d.id}
                                    className="group bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer flex flex-col"
                                    onClick={() => onSelect(d)}
                                >
                                    <div className="aspect-video bg-gray-100 relative overflow-hidden border-b border-gray-100">
                                        {d.thumbnail ? (
                                            <img
                                                src={d.thumbnail}
                                                alt={d.name}
                                                className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                <span className="text-4xl">🖼️</span>
                                            </div>
                                        )}
                                        {d.databaseConfig && (
                                            <div className="absolute top-2 right-2 bg-blue-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
                                                DB
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4 flex-1 flex flex-col">
                                        <h3 className="font-semibold text-gray-800 mb-1 truncate" title={d.name}>{d.name}</h3>
                                        <div className="text-xs text-gray-500 mt-auto flex justify-between items-center">
                                            <span>{new Date(d.updatedAt).toLocaleDateString()}</span>
                                            {d.canvasData?.isDoubleSided && (
                                                <span className="text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">2 Caras</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-100 bg-white flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
}
